import { useEffect, useMemo, useRef, useState } from "react";
import { Plus, Search, Pencil, Trash2, Users, LayoutGrid, List } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { PageHeader, StatusBadge, EmptyState, Field } from "../components/Shared";
import { api, formatApiError } from "../lib/api";
import { CLIENT_STATUS } from "../lib/labels";

const EMPTY = { name: "", type: "particulier", email: "", phone: "", address: "", city: "", status: "prospect", notes: "", siren: "", lang: "fr" };

function ClientDialog({ open, onClose, initial, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [langTouched, setLangTouched] = useState(false);
  const [hint, setHint] = useState("");
  const seq = useRef(0);
  useEffect(() => {
    setForm(initial || EMPTY);
    setLangTouched(false);
    setHint("");
  }, [initial, open]);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target ? e.target.value : e });
  const detectLang = async () => {
    const q = [form.address, form.city].filter(Boolean).join(", ").trim();
    const unchanged = initial && initial.address === form.address && initial.city === form.city;
    if (q.length < 3 || !form.city || unchanged || langTouched) return;
    const n = ++seq.current;
    try {
      const { data } = await api.get("/geo/country", { params: { q } });
      if (n !== seq.current || !data.country_code) return;
      const lang = data.francophone ? "fr" : "en";
      setForm((f) => ({ ...f, lang }));
      setHint(lang === "en" ? `Anglais proposé : adresse en ${data.country}` : "");
    } catch (e) {
      setHint("");
    }
  };

  const save = async () => {
    try {
      const { id, company_id, created_at, ...payload } = form;
      if (id) await api.put(`/clients/${id}`, payload);
      else await api.post("/clients", payload);
      toast.success("Client enregistré");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="client-dialog">
        <DialogHeader><DialogTitle>{form.id ? "Modifier le client" : "Nouveau client"}</DialogTitle></DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="sm:col-span-2"><Field label="Nom / Raison sociale"><Input data-testid="client-name-input" value={form.name} onChange={set("name")} /></Field></div>
          <Field label="Type">
            <Select value={form.type} onValueChange={set("type")}>
              <SelectTrigger data-testid="client-type-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="particulier">Particulier</SelectItem><SelectItem value="entreprise">Entreprise</SelectItem></SelectContent>
            </Select>
          </Field>
          <Field label="Statut">
            <Select value={form.status} onValueChange={set("status")}>
              <SelectTrigger data-testid="client-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(CLIENT_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="E-mail"><Input data-testid="client-email-input" value={form.email} onChange={set("email")} /></Field>
          <Field label="Téléphone"><Input data-testid="client-phone-input" value={form.phone} onChange={set("phone")} /></Field>
          <Field label="Adresse"><Input data-testid="client-address-input" value={form.address} onChange={set("address")} onBlur={detectLang} /></Field>
          <Field label="Ville"><Input data-testid="client-city-input" value={form.city} onChange={set("city")} onBlur={detectLang} /></Field>
          <Field label="Langue du client (e-mails et documents)">
            <Select value={form.lang || "fr"} onValueChange={(v) => { setForm({ ...form, lang: v }); setLangTouched(true); setHint(""); }}>
              <SelectTrigger data-testid="client-lang-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="fr" data-testid="client-lang-fr">Français</SelectItem><SelectItem value="en" data-testid="client-lang-en">English</SelectItem></SelectContent>
            </Select>
            {hint && <p className="mt-1 text-xs font-medium text-teal-700" data-testid="client-lang-hint">{hint}</p>}
          </Field>
          {form.type === "entreprise" && <Field label="SIREN (facturation électronique)"><Input data-testid="client-siren-input" inputMode="numeric" value={form.siren || ""} onChange={set("siren")} placeholder="9 chiffres" /></Field>}
          <div className="sm:col-span-2"><Field label="Notes / historique"><Textarea data-testid="client-notes-input" rows={3} value={form.notes} onChange={set("notes")} /></Field></div>
        </div>
        <DialogFooter><Button data-testid="client-save-button" className="bg-[#0b2c6b]" onClick={save}>Enregistrer</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Kanban({ clients, onEdit }) {
  return (
    <div className="grid gap-4 overflow-x-auto md:grid-cols-5" data-testid="clients-kanban">
      {Object.entries(CLIENT_STATUS).map(([k, v]) => (
        <div key={k} className="min-w-[200px] rounded-xl bg-slate-100/70 p-3" data-testid={`kanban-col-${k}`}>
          <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-500">{v.label} · {clients.filter((c) => c.status === k).length}</p>
          <div className="space-y-2">
            {clients.filter((c) => c.status === k).map((c) => (
              <button key={c.id} onClick={() => onEdit(c)} data-testid={`kanban-card-${c.id}`} className="card-hover w-full rounded-lg border border-slate-200 bg-white p-3 text-left">
                <p className="text-sm font-semibold text-slate-900">{c.name}</p>
                <p className="text-xs text-slate-500">{c.city || "—"} · {c.type}</p>
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [view, setView] = useState("list");
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const load = () => api.get("/clients").then((r) => setClients(r.data));
  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => clients.filter((c) =>
    (status === "all" || c.status === status) &&
    [c.name, c.email, c.city, c.phone].join(" ").toLowerCase().includes(q.toLowerCase())), [clients, q, status]);

  const edit = (c) => { setEditing(c); setOpen(true); };
  const remove = async (c) => {
    if (!window.confirm(`Supprimer ${c.name} ?`)) return;
    await api.delete(`/clients/${c.id}`);
    toast.success("Client supprimé");
    load();
  };

  return (
    <div className="fade-up">
      <PageHeader overline="CRM" title="Clients" subtitle="Particuliers et entreprises, du prospect au déménagement effectué.">
        <Button data-testid="add-client-button" className="bg-[#0b2c6b] hover:bg-[#1559d6]" onClick={() => edit(null)}><Plus className="mr-2 h-4 w-4" />Nouveau client</Button>
      </PageHeader>
      <div className="mb-5 flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input data-testid="clients-search-input" className="bg-white pl-9" placeholder="Rechercher nom, e-mail, ville…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="clients-status-filter" className="bg-white sm:w-52"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Tous les statuts</SelectItem>{Object.entries(CLIENT_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
        </Select>
        <div className="flex rounded-lg border border-slate-200 bg-white p-1">
          <Button size="sm" variant={view === "list" ? "secondary" : "ghost"} data-testid="view-list-button" onClick={() => setView("list")}><List className="h-4 w-4" /></Button>
          <Button size="sm" variant={view === "kanban" ? "secondary" : "ghost"} data-testid="view-kanban-button" onClick={() => setView("kanban")}><LayoutGrid className="h-4 w-4" /></Button>
        </div>
      </div>

      {filtered.length === 0 ? <EmptyState icon={Users} text="Aucun client trouvé" testid="clients-empty" /> : view === "kanban" ? <Kanban clients={filtered} onEdit={edit} /> : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          <Table data-testid="clients-table">
            <TableHeader><TableRow><TableHead>Client</TableHead><TableHead>Contact</TableHead><TableHead>Ville</TableHead><TableHead>Statut</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
            <TableBody>
              {filtered.map((c) => (
                <TableRow key={c.id} data-testid={`client-row-${c.id}`}>
                  <TableCell><p className="font-semibold text-slate-900">{c.name}</p><p className="text-xs capitalize text-slate-500">{c.type}</p></TableCell>
                  <TableCell className="text-sm text-slate-600">{c.email}<br /><span className="text-xs text-slate-400">{c.phone}</span></TableCell>
                  <TableCell className="text-sm">{c.city}</TableCell>
                  <TableCell><StatusBadge map={CLIENT_STATUS} value={c.status} /></TableCell>
                  <TableCell className="text-right">
                    <Button size="icon" variant="ghost" data-testid={`edit-client-${c.id}`} onClick={() => edit(c)}><Pencil className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" data-testid={`delete-client-${c.id}`} onClick={() => remove(c)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <ClientDialog open={open} initial={editing} onClose={() => setOpen(false)} onSaved={load} />
    </div>
  );
}
